import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Phone, Send } from "lucide-react";
import { API, CONTACT, DESTINATIONS } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const initial = { name: "", email: "", phone: "", destination: "", travel_date: "", travelers: "2", message: "" };

export const BookingForm = () => {
    const [params] = useSearchParams();
    const prefill = DESTINATIONS.includes(params.get("dest") || "") ? params.get("dest") : "";
    const [form, setForm] = useState({ ...initial, destination: prefill });
    const [reference, setReference] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/bookings`, {
                ...form,
                travelers: Number(form.travelers),
            });
            setReference(data.id);
            toast.success("Booking enquiry received — we will call you shortly.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit your enquiry. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (reference) {
        return (
            <div className="card-frame p-10 text-center" data-testid="booking-success-panel">
                <CheckCircle2 className="mx-auto h-14 w-14 text-brass" strokeWidth={1.25} />
                <h3 className="mt-6 font-serif text-2xl font-semibold text-navy">Enquiry received</h3>
                <p className="mt-3 text-sm leading-relaxed text-steel">
                    Reference <span className="font-semibold text-navy">{reference}</span>.
                    A travel designer will contact you within 24 hours with a tailored plan and quote.
                </p>
                <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                    <a
                        href={CONTACT.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="booking-whatsapp-button"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
                    >
                        <WhatsAppIcon className="h-4 w-4" />
                        Chat on WhatsApp
                    </a>
                    <a
                        href={CONTACT.phoneHref}
                        data-testid="booking-call-button"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-navy-800"
                    >
                        <Phone className="h-4 w-4 text-brass" />
                        Call {CONTACT.phoneDisplay}
                    </a>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="card-frame p-8 sm:p-10" data-testid="booking-form">
            <h3 className="font-serif text-2xl font-semibold text-navy">Book your journey</h3>
            <p className="mt-2 text-sm text-steel">Tell us where you dream of going — we handle the rest.</p>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-1">
                    <Label htmlFor="bk-name" className="text-xs font-semibold uppercase tracking-wider text-steel">Full name</Label>
                    <Input id="bk-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Aarav Sharma" className="input-classic mt-2" data-testid="booking-name-input" />
                </div>
                <div className="sm:col-span-1">
                    <Label htmlFor="bk-email" className="text-xs font-semibold uppercase tracking-wider text-steel">Email</Label>
                    <Input id="bk-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="input-classic mt-2" data-testid="booking-email-input" />
                </div>
                <div className="sm:col-span-1">
                    <Label htmlFor="bk-phone" className="text-xs font-semibold uppercase tracking-wider text-steel">Phone</Label>
                    <Input id="bk-phone" required value={form.phone} onChange={set("phone")} placeholder="+91 98XXX XXXXX" className="input-classic mt-2" data-testid="booking-phone-input" />
                </div>
                <div className="sm:col-span-1">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-steel">Destination</Label>
                    <Select required value={form.destination} onValueChange={(v) => setForm((f) => ({ ...f, destination: v }))}>
                        <SelectTrigger className="input-classic mt-2" data-testid="booking-destination-select">
                            <SelectValue placeholder="Choose a destination" />
                        </SelectTrigger>
                        <SelectContent>
                            {DESTINATIONS.map((d) => (
                                <SelectItem key={d} value={d} data-testid={`destination-option-${d.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                    {d}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="sm:col-span-1">
                    <Label htmlFor="bk-date" className="text-xs font-semibold uppercase tracking-wider text-steel">Preferred travel date</Label>
                    <Input id="bk-date" required type="date" value={form.travel_date} onChange={set("travel_date")} className="input-classic mt-2" data-testid="booking-date-input" />
                </div>
                <div className="sm:col-span-1">
                    <Label htmlFor="bk-travelers" className="text-xs font-semibold uppercase tracking-wider text-steel">Travellers</Label>
                    <Input id="bk-travelers" required type="number" min="1" max="100" value={form.travelers} onChange={set("travelers")} className="input-classic mt-2" data-testid="booking-travelers-input" />
                </div>
                <div className="sm:col-span-2">
                    <Label htmlFor="bk-message" className="text-xs font-semibold uppercase tracking-wider text-steel">Tell us about your trip (optional)</Label>
                    <Textarea id="bk-message" rows={4} value={form.message} onChange={set("message")} placeholder="Trip length, experiences you love, budget in mind…" className="input-classic mt-2 resize-none" data-testid="booking-message-input" />
                </div>
            </div>

            <Button
                type="submit"
                disabled={loading}
                data-testid="booking-submit-button"
                className="mt-8 w-full rounded-full bg-brass py-4 text-sm font-semibold text-navy transition-all duration-300 hover:bg-brass-dark disabled:opacity-60"
            >
                {loading ? "Sending…" : (
                    <span className="inline-flex items-center gap-2">
                        Submit Booking Enquiry <Send className="h-4 w-4" />
                    </span>
                )}
            </Button>
            <p className="mt-4 text-center text-xs text-steel/70">No advance payment now — our designer will call you to tailor the plan.</p>
        </form>
    );
};
